import { useEffect, useMemo, useRef } from "react";

/**
 * The one-line "here's what I'm doing" caption shown under a spinner while an agent works.
 *
 * Extracted from `AskCoffeePanel` / `MobileAskCoffee`, which carried identical copies. The
 * agent builder is the third consumer, and a third copy is how the three drift apart.
 *
 * Two rules, both learned from Ask Coffee:
 *
 * 1. **Labels are rendered verbatim.** They come from the backend — an MCP tool's
 *    `meta.progress_label` for Ask Coffee, a `ToolSpec.progress_label` for the builder — so
 *    adding or renaming a tool never touches this file. Nothing here maps tool names.
 * 2. **The last label is sticky.** Tool calls are fast; the model's reasoning *between* them
 *    dominates the wall clock and leaves the active list empty. Blanking the caption in those
 *    gaps makes a working agent look stalled, so the previous line stays — the model is still
 *    digesting that tool's output, which is what the line already says.
 */
export interface ThinkingSubtextOptions {
  /** Shown before the first label arrives, and if a run somehow produces none. */
  idle: string;
  /** Shown instead of an unreadable pile-up once three or more run at once. */
  many?: string;
  /**
   * Append "…" to the caption, marking it as an action still in progress.
   *
   * Applied here rather than baked into the labels themselves because the labels come from three
   * different backends (MCP tool annotations, the agent service's local table, the CRM's
   * ToolSpec.progress_label) and every future label author would have to remember. One rule, one
   * place. Phrases that already end in their own punctuation are left alone, so a fallback like
   * "Drafting…" never becomes "Drafting……".
   */
  ellipsis?: boolean;
}

/** True when the phrase already terminates itself and needs no "…" appended. */
const ALREADY_TERMINATED = /[….!?:]$/;

const withEllipsis = (phrase: string, on: boolean): string =>
  on && phrase && !ALREADY_TERMINATED.test(phrase) ? `${phrase}…` : phrase;

export function useThinkingSubtext(
  activeLabels: readonly string[] | undefined,
  active: boolean,
  { idle, many = "Searching your CRM", ellipsis = false }: ThinkingSubtextOptions,
): string {
  const lastPhraseRef = useRef("");

  // Reset per run, so one run's last line never leads the next.
  useEffect(() => {
    if (!active) lastPhraseRef.current = "";
  }, [active]);

  return useMemo(() => {
    const labels = Array.from(new Set(activeLabels ?? []));
    if (labels.length === 0) return withEllipsis(lastPhraseRef.current || idle, ellipsis);
    const phrase =
      labels.length === 1
        ? labels[0]
        : labels.length === 2
          ? `${labels[0]} & ${labels[1].toLowerCase()}`
          : many;
    // Store the BARE phrase; the suffix is presentation and must not accumulate on re-render.
    lastPhraseRef.current = phrase;
    return withEllipsis(phrase, ellipsis);
    // `active` participates so the caption re-evaluates when a run starts/stops, matching the
    // original behaviour in both Ask Coffee surfaces.
  }, [activeLabels, active, idle, many, ellipsis]);
}
